import type { ReactNode } from "react";

import { cn } from "./lib/cn";

export interface ListRowProps {
  /** 36px kutu, muted zemin. */
  icon?: ReactNode;
  title: string;
  /** Rozet gibi sayaç — başlığın hemen sağında. `Badge tone="neutral"` beklenir. */
  count?: ReactNode;
  subtitle?: ReactNode;
  /** Sağdaki ikincil bilgi (mockup: tarih, "5 gün kaldı"). `action` ile birlikte kullanılmaz. */
  meta?: ReactNode;
  /**
   * Sağdaki accent bağlantı ("İncele ›", "Görüntüle ›"). Yönlendirme bu paketin işi DEĞİL
   * (`NavItem` ile aynı kural, `packages/ui/CLAUDE.md`): çağıran ya `onClick` verir ya da
   * bütün satırı kendi `<Link>`'inin içine koyar.
   */
  action?: { label: string; onClick?: () => void };
  /** Serbest sağ yuva — `action`/`meta` yetmediğinde. */
  trailing?: ReactNode;
  className?: string;
}

/**
 * Liste satırı — UI Tur 2 §5. Mockup'ın en çok tekrar eden kalıbı: ikon/başlık/alt metin
 * solda, sağda ikincil bilgi ya da aksiyon. `packages/ui/CLAUDE.md`: bileşen domain BİLMEZ —
 * "Bekleyen İşlemler" mi "Davetlerim" mi olduğunu bilmiyor.
 *
 * ⚠ Satırın TAMAMI bir bağlantı DEĞİL (mockup'ta yalnız sağdaki "İncele ›" tıklanıyor,
 * ikon/başlık salt bilgi). Bütün satırı tıklanabilir yapmak gereken ekranda çağıran
 * bileşeni kendi `<Link>`'ine sarar — iç içe interaktif eleman (satır linki + aksiyon
 * linki) a11y ihlali olurdu.
 */
export function ListRow({
  icon,
  title,
  count,
  subtitle,
  meta,
  action,
  trailing,
  className,
}: ListRowProps) {
  return (
    <div
      className={cn(
        "flex h-16 items-center gap-3 rounded-lg border border-border-subtle px-3.5 transition-colors hover:bg-muted",
        className,
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-fg-soft [&_svg]:size-[18px]"
        >
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-fg-strong">{title}</span>
          {count}
        </span>
        {subtitle ? (
          <span className="block truncate text-[13px] text-fg-muted">{subtitle}</span>
        ) : null}
      </div>
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          className="flex shrink-0 items-center gap-1 text-[13px] font-medium text-accent hover:text-accent-hover"
        >
          {action.label}
          <svg viewBox="0 0 20 20" fill="currentColor" className="size-4" aria-hidden="true">
            <path d="M7.21 4.29a.75.75 0 0 1 1.06 0l5 5a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 1 1-1.06-1.06L11.69 10 7.21 5.35a.75.75 0 0 1 0-1.06Z" />
          </svg>
        </button>
      ) : meta ? (
        <span className="shrink-0 text-xs text-fg-muted">{meta}</span>
      ) : trailing ? (
        <span className="shrink-0">{trailing}</span>
      ) : null}
    </div>
  );
}
